import { Link } from 'react-router-dom';
import {
  Network,
  Share2,
  History,
  Shield,
  ArrowUpRight,
  Sparkles,
  Layers,
  PenTool,
  ChevronRight,
} from 'lucide-react';

const FEATURES = [
  {
    icon: PenTool,
    title: '富文本文档',
    desc: '支持标题、列表、代码块、引用、任务清单等丰富格式，专注内容创作。',
  },
  {
    icon: Network,
    title: '实时协同编辑',
    desc: '基于 CRDT 技术，多人同时编辑零冲突，光标与选区实时可见。',
  },
  {
    icon: Layers,
    title: '思维导图',
    desc: '可视化梳理思路，节点自由拖拽，与文档无缝切换。',
  },
  {
    icon: Share2,
    title: '分享与权限',
    desc: '三档权限控制、密码保护、有效期设置，安全分享给团队。',
  },
  {
    icon: History,
    title: '版本历史',
    desc: '自动快照保存，任意时间点安全回滚，再也不怕误操作。',
  },
  {
    icon: Shield,
    title: '边缘加速',
    desc: '基于 Cloudflare 全球边缘网络，数据加密传输，低延迟响应。',
  },
];

const STATS = [
  { value: '50ms', label: '全球延迟' },
  { value: '99.9%', label: '可用性' },
  { value: '∞', label: '协作者' },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* ===== NAV ===== */}
      <header className="sticky top-0 z-30 border-b border-[#e6e7eb]/60 bg-[#f8f9fa]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-6 sm:px-10">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#5b5fc7] text-xs font-bold text-white shadow-sm">
              C
            </div>
            <span className="text-[15px] font-semibold tracking-tight">CoMind</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex text-sm font-medium text-[#4a4a5a] hover:text-[#1a1a2e] transition-colors"
            >
              登录
            </Link>
            <Link
              to="/register"
              className="inline-flex h-8 items-center rounded-lg bg-[#1a1a2e] px-4 text-sm font-medium text-white hover:bg-[#2a2a3e] shadow-sm transition-all"
            >
              免费开始
            </Link>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden>
          <div className="absolute left-1/2 top-0 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-[#5b5fc7]/[0.03] blur-3xl" />
          <div className="absolute right-0 top-40 h-56 w-56 rounded-full bg-[#5b5fc7]/[0.04] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 sm:px-10 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#e6e7eb] bg-white/80 px-4 py-1 text-[13px] font-medium text-[#4a4a5a] shadow-sm backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#5b5fc7]" />
            面向小团队的协作工具
          </div>

          <h1 className="max-w-3xl text-4xl font-bold tracking-tight leading-[1.1] sm:text-5xl lg:text-6xl text-[#1a1a2e] text-balance">
            文档与思维导图
            <br />
            <span className="text-[#5b5fc7]">一个工具搞定</span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4a4a5a] sm:text-lg">
            集成富文本编辑器与思维导图，基于 CRDT 实现多人实时协同。
            <br className="hidden sm:block" />
            全球边缘网络加速，享受低延迟的协作体验。
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="group inline-flex h-11 items-center gap-2 rounded-lg bg-[#5b5fc7] px-6 text-sm font-semibold text-white shadow-md shadow-[#5b5fc7]/20 hover:bg-[#4b4fa8] hover:shadow-lg hover:shadow-[#5b5fc7]/25 transition-all"
            >
              免费开始使用
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#e6e7eb] bg-white px-6 text-sm font-medium text-[#1a1a2e] hover:bg-[#f0f1f3] transition-colors"
            >
              已有账号？登录
            </Link>
          </div>

          <p className="mt-4 text-[13px] text-[#9a9aaf]">无需信用卡 · 30 秒开始</p>

          <div className="mt-16 relative">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#f8f9fa] via-transparent to-transparent z-10 pointer-events-none" />
            <div className="overflow-hidden rounded-2xl bg-white shadow-xl shadow-[#1a1a2e]/[0.04] ring-1 ring-black/5">
              <img
                src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&auto=format&fit=crop&q=80"
                alt="CoMind 产品界面预览"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="border-y border-[#e6e7eb] bg-white">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-10">
          <div className="grid grid-cols-3 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-bold tracking-tight text-[#1a1a2e] sm:text-3xl">
                  {s.value}
                </div>
                <div className="mt-1 text-[13px] text-[#9a9aaf]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="bg-[#f8f9fa]">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-20 sm:py-28">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight text-[#1a1a2e] sm:text-4xl">
              为什么选择 CoMind？
            </h2>
            <p className="mt-3 text-base text-[#4a4a5a]">
              集文档编辑、思维导图、团队协作为一体的知识管理平台
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="group relative rounded-xl bg-white p-7 shadow-sm ring-1 ring-black/5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <span className="absolute top-5 right-5 text-[11px] font-mono text-[#d0d1d8] group-hover:text-[#9a9aaf] transition-colors">
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eeeffb] text-[#5b5fc7] group-hover:scale-110 transition-transform duration-300">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-[15px] font-semibold text-[#1a1a2e]">{f.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#4a4a5a]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-[#1a1a2e]">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-20 sm:py-24">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-lg">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                准备好开始了吗？
              </h2>
              <p className="mt-3 text-base text-[#9a9aaf]">
                免费创建你的第一个工作空间，邀请团队成员一起协作
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/register"
                className="group inline-flex h-11 items-center gap-2 rounded-lg bg-white px-6 text-sm font-semibold text-[#1a1a2e] hover:bg-[#f0f1f3] transition-colors shadow-sm"
              >
                免费开始使用
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-[#e6e7eb] bg-[#f8f9fa]">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-6 sm:px-10 text-[12px] text-[#9a9aaf]">
          <span>© 2026 CoMind</span>
          <div className="flex items-center gap-4">
            <button type="button" className="hover:text-[#4a4a5a] transition-colors">
              隐私政策
            </button>
            <button type="button" className="hover:text-[#4a4a5a] transition-colors">
              服务条款
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
